import { useEffect, useRef } from 'react';

interface Props {
  onClose: () => void;
}

interface ShortcutGroup {
  label: string;
  icon: string;
  shortcuts: { keys: string[]; description: string }[];
}

const SHORTCUT_GROUPS: ShortcutGroup[] = [
  {
    label: 'Full-Screen Preview',
    icon: 'ri-fullscreen-line',
    shortcuts: [
      { keys: ['←'], description: 'Previous spread / page' },
      { keys: ['→'], description: 'Next spread / page' },
      { keys: ['Esc'], description: 'Close full-screen preview' },
      { keys: ['R'], description: 'Toggle Reading Mode (sepia)' },
    ],
  },
  {
    label: 'Template Actions',
    icon: 'ri-book-2-line',
    shortcuts: [
      { keys: ['S'], description: 'Save / unfave highlighted template' },
      { keys: ['C'], description: 'Add to Compare (max 3)' },
      { keys: ['P'], description: 'Open full-screen preview' },
      { keys: ['Enter'], description: 'Open template details' },
    ],
  },
  {
    label: 'Navigation & Search',
    icon: 'ri-compass-3-line',
    shortcuts: [
      { keys: ['/'], description: 'Focus search bar' },
      { keys: ['↑', '↓'], description: 'Navigate search suggestions' },
      { keys: ['↵'], description: 'Select search suggestion' },
      { keys: ['Esc'], description: 'Clear search / close dropdown' },
    ],
  },
  {
    label: 'Modals & Panels',
    icon: 'ri-layout-2-line',
    shortcuts: [
      { keys: ['Esc'], description: 'Close any open modal' },
      { keys: ['?'], description: 'Show / hide this shortcuts panel' },
      { keys: ['F'], description: 'Open My Favorites drawer' },
      { keys: ['G'], description: 'Open Generate eBook modal' },
    ],
  },
];

export default function KeyboardShortcutsPanel({ onClose }: Props) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'Escape' || e.key === '?') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // click outside to close
  const handleBackdrop = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      onClick={handleBackdrop}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <div
        ref={panelRef}
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden"
        style={{ maxHeight: '90vh' }}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex items-center justify-between border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center shadow-md">
              <i className="ri-keyboard-line text-white text-lg"></i>
            </div>
            <div>
              <h3 className="text-lg font-extrabold text-slate-900">Keyboard Shortcuts</h3>
              <p className="text-xs text-slate-400">Press <kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded text-slate-500 font-mono text-xs">?</kbd> anytime to open this panel</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-xl border border-slate-200 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-all cursor-pointer"
          >
            <i className="ri-close-line text-base"></i>
          </button>
        </div>

        {/* Shortcuts Grid */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 100px)' }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {SHORTCUT_GROUPS.map(group => (
              <div key={group.label} className="bg-slate-50 rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 bg-white rounded-lg flex items-center justify-center border border-slate-200">
                    <i className={`${group.icon} text-amber-500 text-sm`}></i>
                  </div>
                  <h4 className="text-sm font-extrabold text-slate-800">{group.label}</h4>
                </div>
                <div className="space-y-2">
                  {group.shortcuts.map((shortcut, idx) => (
                    <div key={idx} className="flex items-center justify-between gap-3">
                      <span className="text-xs text-slate-600 flex-1">{shortcut.description}</span>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {shortcut.keys.map((key, ki) => (
                          <span key={ki} className="flex items-center gap-1">
                            <kbd className="px-2 py-1 bg-white border-2 border-slate-200 rounded-lg text-xs font-mono font-bold text-slate-700 shadow-sm min-w-[28px] text-center">
                              {key}
                            </kbd>
                            {ki < shortcut.keys.length - 1 && (
                              <span className="text-xs text-slate-400">+</span>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Footer tip */}
          <div className="mt-5 flex items-center gap-3 p-4 bg-amber-50 rounded-2xl border border-amber-100">
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
              <i className="ri-lightbulb-line text-amber-500 text-lg"></i>
            </div>
            <div>
              <p className="text-xs font-bold text-amber-800">Pro Tip</p>
              <p className="text-xs text-amber-700 mt-0.5 leading-relaxed">
                Shortcuts work anywhere on the page. In the full-screen reader, use arrow keys to flip pages and <kbd className="px-1 py-0.5 bg-amber-100 border border-amber-200 rounded text-amber-700 font-mono text-xs">R</kbd> to switch between sepia and dark mode.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
